import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { useIsMobile } from '@/hooks/use-mobile'

interface SideNavContextType {
  isMainNavCollapsed: boolean
  isSecondaryNavCollapsed: boolean
  isNavCollapsed: boolean
  hasSecondaryNav: boolean
}

const SideNavContext = createContext<
  [SideNavContextType, Dispatch<SetStateAction<SideNavContextType>>]
>([
  {
    isMainNavCollapsed: false,
    isSecondaryNavCollapsed: false,
    hasSecondaryNav: false,
    isNavCollapsed: false,
  },
  () => {},
])

// Initial state factory - main nav collapsed but secondary nav visible.
// On mobile, start with the nav collapsed so the floating pill shows and the
// content is visible; otherwise the full sidebar + backdrop overlay mounts on
// first load (covering the page) until the user happens to navigate via a link.
const getInitialState = (width: number): SideNavContextType => ({
  isMainNavCollapsed: true,
  isSecondaryNavCollapsed: false,
  hasSecondaryNav: false,
  isNavCollapsed: width < 768,
})

export const SideNavProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // ⚠️ Never `useWindowSize` (or any per-pixel width state) here. This
  // provider sits at the ROOT of the app, so per-resize-event state made the
  // ENTIRE tree re-render on every frame of a window drag — the app-wide
  // "everything flickers while resizing" bug. All this provider needs is the
  // 768px threshold; `useIsMobile` only changes state when it's crossed.
  const isMobile = useIsMobile()

  // Track previous mobile state to detect actual transitions
  const prevIsMobileRef = useRef<boolean>(isMobile)
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  const [navState, setNavStateBase] = useState<SideNavContextType>(() =>
    getInitialState(window.innerWidth)
  )

  // Backwards-compatible state setter that merges updates
  const setNavState: Dispatch<SetStateAction<SideNavContextType>> = useCallback(
    (stateOrUpdater: any) => {
      setNavStateBase((prev: any) => {
        const nextState =
          typeof stateOrUpdater === 'function'
            ? (stateOrUpdater as (p: SideNavContextType) => SideNavContextType)(
                prev
              )
            : stateOrUpdater
        return { ...prev, ...nextState }
      })
    },
    []
  )

  // Initialize state when switching between mobile/desktop. `isMobile` only
  // flips when the 768px threshold is crossed, so this effect is quiet during
  // a resize drag; the short debounce just smooths rapid back-and-forth
  // threshold crossings.
  useEffect(() => {
    // Clear any pending resize handler
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current)
    }

    resizeTimeoutRef.current = setTimeout(() => {
      const wasMobile = prevIsMobileRef.current
      if (wasMobile !== isMobile) {
        setNavStateBase(getInitialState(window.innerWidth))
        prevIsMobileRef.current = isMobile
      }
    }, 150) // 150ms debounce to stabilize during resize

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }
    }
  }, [isMobile])

  // Don't auto-collapse on route changes - let user control sidebar state
  // The sidebar will only close when:
  // 1. User clicks the backdrop (handled in sidebar component)
  // 2. User clicks the close button (handled in sidebar component)
  // 3. User navigates via a NavLink (can be handled there if needed)

  const contextValue = useMemo(
    () =>
      [navState, setNavState] as [
        SideNavContextType,
        Dispatch<SetStateAction<SideNavContextType>>,
      ],
    [navState, setNavState]
  )

  return (
    <SideNavContext.Provider value={contextValue}>
      {children}
    </SideNavContext.Provider>
  )
}

export const useSideNavContext = () => useContext(SideNavContext)
